/**
 * Shared Filter Utilities
 *
 * This file contains shared filter-related utilities used across multiple features:
 * - Date range filter calculations
 * - Filter label formatters
 *
 * All dates are Unix timestamps in milliseconds (Date.now() format).
 */

import { CommonDateFilter } from '../types/filters';

import { getEndOfToday, getStartOfDaysAgo,getStartOfToday } from './time';

/**
 * Get date range for common date filter
 * Returns { gte, lte } for the filter range, or null if invalid
 *
 * @param filter - CommonDateFilter enum value
 * @returns Object with gte (greater than or equal) and optional lte (less than or equal) timestamps, or null if invalid
 */
export function getCommonDateRange(filter: CommonDateFilter): { gte: number; lte?: number } | null {
	const startOfToday = getStartOfToday();
	const endOfToday = getEndOfToday();

	switch (filter) {
		case CommonDateFilter.TODAY:
			return { gte: startOfToday, lte: endOfToday };
		case CommonDateFilter.LAST_7_DAYS:
			return { gte: getStartOfDaysAgo(7), lte: endOfToday };
		case CommonDateFilter.LAST_30_DAYS:
			return { gte: getStartOfDaysAgo(30), lte: endOfToday };
		case CommonDateFilter.PLUS_30_DAYS:
			// 30+ days ago means anything before 30 days ago
			return { gte: 0, lte: getStartOfDaysAgo(30) - 1 };
		case CommonDateFilter.CUSTOM:
			// Custom range is handled separately via custom range atoms
			return null;
		default:
			return null;
	}
}

/**
 * Format common date filter label for display
 *
 * @param filter - CommonDateFilter enum value
 * @returns Human-readable label for the filter
 */
export function formatCommonDateFilterLabel(filter: CommonDateFilter): string {
	switch (filter) {
		case CommonDateFilter.TODAY:
			return 'Today';
		case CommonDateFilter.LAST_7_DAYS:
			return 'Last 7 days';
		case CommonDateFilter.LAST_30_DAYS:
			return 'Last 30 days';
		case CommonDateFilter.PLUS_30_DAYS:
			return '30+ days';
		case CommonDateFilter.CUSTOM:
			return 'Custom';
		default:
			return filter;
	}
}
